import {
  CALENDAR_VIEW_LABELS,
  type CalendarView,
  DELIVERABLE_STATUS_LABELS,
  DELIVERABLE_STATUSES,
  type DeliverableStatus,
  type GroupByOption,
} from "@dream/config/constants";
import {
  Button,
  IconBolt,
  IconChevronLeft,
  IconChevronRight,
  IconClock,
  IconClose,
  IconFilter,
  IconGoogle,
  IconPlus,
  IconSearch,
  IconShare,
  IconViewAgenda,
  IconViewDay,
  IconViewMonth,
  IconViewTimeline,
  IconViewWeek,
  Input,
  MenuSelect,
  useSlidingPill,
} from "@dream/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  type ReactElement,
  type ReactNode,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { ConnectionState } from "../hooks/use-realtime.ts";
import { useTimelineDays } from "../hooks/use-viewport.ts";
import { useAdminChromeOptional } from "../layouts/admin-chrome.tsx";
import { usePreferences } from "../preferences/provider.tsx";
import type { CalendarSource } from "../router.tsx";
import {
  type CalendarFilterDimension,
  countActiveCalendarFilters,
  formatPeriodLabel,
  listActiveCalendarFilterChips,
} from "./calendar-chrome.ts";
import { OverflowMenu } from "./overflow-menu.tsx";
import { TypeIcon } from "./type-icon.tsx";

/**
 * Barra de navegação e filtros do calendário.
 *
 * Toda mudança aqui vira alteração de search param na URL. Os filtros avançados
 * abrem num popover compacto preso ao botão — um sheet ou uma faixa de selects
 * ocupava a tela e escondia o calendário.
 */

const VIEW_ICONS: Record<CalendarView, ReactNode> = {
  dia: <IconViewDay className="size-3.5" />,
  semana: <IconViewWeek className="size-3.5" />,
  mes: <IconViewMonth className="size-3.5" />,
  agenda: <IconViewAgenda className="size-3.5" />,
  linha: <IconViewTimeline className="size-3.5" />,
};

const GROUP_LABELS: Record<GroupByOption, string> = {
  none: "Sem agrupamento",
  client: "Por cliente",
  project: "Por projeto",
  assignee: "Por responsável",
};

export interface CalendarToolbarProps {
  view: CalendarView;
  focusDate: Date;
  timezone: string;
  groupBy: GroupByOption;
  colorBy: "type" | "client";
  search: string;
  statuses: DeliverableStatus[];
  clientIds: string[];
  clients: Array<{ id: string; name: string; color: string }>;
  connection?: ConnectionState;
  loading?: boolean;
  resultCount: number;
  googleCount?: number;
  googleConnected?: boolean;
  googleSyncedAt?: string | null;
  overlaySyncing?: boolean;
  fonte?: CalendarSource;
  readOnly?: boolean;
  onViewChange: (view: CalendarView) => void;
  onNavigate: (direction: "prev" | "next" | "today") => void;
  onGroupByChange: (groupBy: GroupByOption) => void;
  onColorByChange: (colorBy: "type" | "client") => void;
  onSearchChange: (search: string) => void;
  onStatusesChange: (statuses: DeliverableStatus[]) => void;
  onClientIdsChange: (clientIds: string[]) => void;
  onCreate?: () => void;
  onShare?: () => void;
  onClearFilters: () => void;
  onFonteChange?: (fonte: CalendarSource) => void;
  projectIds?: string[];
  typeIds?: string[];
  assigneeIds?: string[];
  projects?: Array<{ id: string; name: string; clientId: string }>;
  types?: Array<{ id: string; label: string; color: string; slug: string; icon: string | null }>;
  team?: Array<{ id: string; name: string }>;
  onProjectIdsChange?: (projectIds: string[]) => void;
  onTypeIdsChange?: (typeIds: string[]) => void;
  onAssigneeIdsChange?: (assigneeIds: string[]) => void;
  currentUserId?: string | null;
  /** No portal o período não compete com o nome do cliente como H1. */
  periodAsHeading?: boolean;
}

function useCompactChrome(): boolean {
  return useSyncExternalStore(
    (onStoreChange) => {
      const media = window.matchMedia("(max-width: 1023px)");
      media.addEventListener("change", onStoreChange);
      return () => media.removeEventListener("change", onStoreChange);
    },
    () => window.matchMedia("(max-width: 1023px)").matches,
    () => false,
  );
}

function ConnectionPill({
  state,
  googleConnected,
  syncAge,
  overlaySyncing,
}: {
  state: ConnectionState;
  googleConnected?: boolean;
  syncAge?: string | null;
  overlaySyncing?: boolean;
}) {
  const config = {
    open: {
      label: overlaySyncing ? "Sincronizando" : "Ao vivo",
      tone: "text-[var(--dream-success-fg)] bg-[var(--dream-success-bg)] border-[var(--dream-success-border)]",
      dot: "bg-[var(--dream-success-fg)]",
    },
    connecting: {
      label: "Conectando",
      tone: "text-[var(--color-ink-muted)] bg-[var(--color-surface-muted)] border-[var(--color-line)]",
      dot: "bg-[var(--color-ink-subtle)]",
    },
    reconnecting: {
      label: "Reconectando",
      tone: "text-[var(--dream-warning-fg)] bg-[var(--dream-warning-bg)] border-[var(--dream-warning-border)]",
      dot: "bg-[var(--dream-warning-fg)]",
    },
    offline: {
      label: "Offline",
      tone: "text-[var(--dream-danger-fg)] bg-[var(--dream-danger-bg)] border-[var(--dream-danger-border)]",
      dot: "bg-[var(--dream-danger-fg)]",
    },
  }[state];

  const title =
    state === "open"
      ? "Atualizações em tempo real ativas."
      : "Sem tempo real. Os dados continuam sendo recarregados do servidor.";

  return (
    <span
      title={title}
      className={`inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full border px-2 text-[0.6875rem] font-medium transition-colors duration-[var(--dream-duration-fast)] ${config.tone}`}
    >
      <span
        aria-hidden="true"
        className={`size-1.5 rounded-full ${config.dot} ${state === "open" || overlaySyncing ? "animate-pulse motion-reduce:animate-none" : ""}`}
      />
      {config.label}
      {state === "open" && googleConnected && syncAge !== null && syncAge !== undefined ? (
        <span className="hidden font-normal text-current/80 xl:inline">· Agenda {syncAge}</span>
      ) : null}
    </span>
  );
}

function formatSyncAge(iso: string | null): string | null {
  if (iso === null || iso === "") return null;
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return null;
  const delta = Math.max(0, Date.now() - parsed);
  if (delta < 45_000) return "agora";
  if (delta < 90_000) return "há 1 min";
  if (delta < 3_600_000) return `há ${Math.round(delta / 60_000)} min`;
  return `há ${Math.round(delta / 3_600_000)} h`;
}

export function CalendarToolbar(props: CalendarToolbarProps) {
  const {
    view,
    focusDate,
    timezone,
    groupBy,
    colorBy,
    search,
    statuses,
    clientIds,
    clients,
    connection,
    loading = false,
    resultCount,
    googleCount = 0,
    googleConnected = false,
    googleSyncedAt = null,
    overlaySyncing = false,
    fonte = "todas",
    readOnly = false,
    projectIds = [],
    typeIds = [],
    assigneeIds = [],
    projects = [],
    types = [],
    team = [],
    periodAsHeading = true,
  } = props;

  const countLabel = (() => {
    if (loading) return "carregando...";
    if (fonte === "agenda") {
      return `${googleCount} ${googleCount === 1 ? "compromisso da Agenda" : "compromissos da Agenda"}`;
    }
    const deliverables = `${resultCount} ${resultCount === 1 ? "entrega" : "entregas"}`;
    if (fonte === "entregas" || googleCount <= 0) return deliverables;
    return `${deliverables} · ${googleCount} da Agenda`;
  })();
  const syncAge = formatSyncAge(googleSyncedAt);
  const compact = useCompactChrome();
  const timelineDays = useTimelineDays();
  const chrome = useAdminChromeOptional();
  const navigate = useNavigate();
  const { prefs, setPrefs } = usePreferences();
  const filterCount = countActiveCalendarFilters({
    search,
    statuses,
    clientIds,
    projectIds,
    typeIds,
    assigneeIds,
    groupBy,
  });
  const hasFilters = filterCount > 0;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);
  const filtersButtonRef = useRef<HTMLButtonElement>(null);

  const firstStatus = statuses[0];
  const firstClientId = clientIds[0];
  const firstProjectId = projectIds[0];
  const firstTypeId = typeIds[0];
  const firstAssigneeId = assigneeIds[0];
  const chips = listActiveCalendarFilterChips({
    statuses,
    statusLabel:
      statuses.length === 1 && firstStatus !== undefined
        ? (DELIVERABLE_STATUS_LABELS[firstStatus] ?? firstStatus)
        : null,
    clientIds,
    clientLabel:
      clientIds.length === 1
        ? (clients.find((client) => client.id === firstClientId)?.name ?? null)
        : null,
    projectIds,
    projectLabel:
      projectIds.length === 1
        ? (projects.find((project) => project.id === firstProjectId)?.name ?? null)
        : null,
    typeIds,
    typeLabel:
      typeIds.length === 1 ? (types.find((type) => type.id === firstTypeId)?.label ?? null) : null,
    assigneeIds,
    assigneeLabel:
      assigneeIds.length === 1
        ? firstAssigneeId === props.currentUserId
          ? "Suas tarefas"
          : (team.find((member) => member.id === firstAssigneeId)?.name ?? null)
        : null,
    groupBy,
    groupByLabel: groupBy === "none" ? null : GROUP_LABELS[groupBy],
  });

  useEffect(() => {
    if (!filtersOpen) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setFiltersOpen(false);
    };
    const onPointer = (event: PointerEvent): void => {
      const node = event.target;
      if (!(node instanceof Element)) return;
      if (filtersRef.current?.contains(node) === true) return;
      if (filtersButtonRef.current?.contains(node) === true) return;
      if (node.closest("[data-radix-menu-content]") !== null) return;
      setFiltersOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [filtersOpen]);

  function clearDimension(dimension: CalendarFilterDimension): void {
    switch (dimension) {
      case "status":
        props.onStatusesChange([]);
        break;
      case "client":
        props.onClientIdsChange([]);
        break;
      case "project":
        props.onProjectIdsChange?.([]);
        break;
      case "type":
        props.onTypeIdsChange?.([]);
        break;
      case "assignee":
        props.onAssigneeIdsChange?.([]);
        break;
      case "groupBy":
        props.onGroupByChange("none");
        break;
    }
  }

  const periodLabel = formatPeriodLabel(view, focusDate, timezone, compact, timelineDays);

  return (
    <div
      className="shrink-0 border-b border-[var(--color-line)] bg-[var(--color-surface)]"
      data-testid="calendar-toolbar"
    >
      <div className="flex flex-col gap-2 px-3 py-2 sm:px-4 lg:flex-row lg:flex-wrap lg:items-center lg:gap-x-3 lg:gap-y-2 lg:px-5 lg:py-3">
        <div className="flex min-w-0 shrink-0 items-center gap-1.5 sm:gap-2">
          {periodAsHeading ? (
            <h1
              aria-live="polite"
              className="max-w-[11.5rem] truncate whitespace-nowrap text-[15px] font-bold tracking-[0] text-[var(--color-ink)] sm:max-w-[16rem] lg:max-w-[18rem] lg:text-[16px]"
            >
              <span key={periodLabel} className="dream-period-in truncate">
                {periodLabel}
              </span>
            </h1>
          ) : (
            <p
              aria-live="polite"
              className="max-w-[11.5rem] truncate whitespace-nowrap text-[15px] font-bold tracking-[0] text-[var(--color-ink)] sm:max-w-[16rem] lg:max-w-[18rem] lg:text-[16px]"
            >
              <span key={periodLabel} className="dream-period-in truncate">
                {periodLabel}
              </span>
            </p>
          )}
          <div className="flex shrink-0 items-center">
            <button
              type="button"
              aria-label="Período anterior"
              onClick={() => props.onNavigate("prev")}
              className="dream-icon-btn size-10 lg:size-8"
            >
              <IconChevronLeft className="size-4" />
            </button>
            <button
              type="button"
              aria-label="Próximo período"
              onClick={() => props.onNavigate("next")}
              className="dream-icon-btn size-10 lg:size-8"
            >
              <IconChevronRight className="size-4" />
            </button>
          </div>
          <button
            type="button"
            onClick={() => props.onNavigate("today")}
            className="min-h-10 shrink-0 rounded-md px-2 text-[13px] font-semibold text-[#0074FF] transition-[background-color] duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-standard)] hover:bg-brand-50 sm:text-[14px] lg:min-h-8"
          >
            Hoje
          </button>

          <div className="ml-auto flex shrink-0 items-center gap-1.5 lg:hidden">
            {compact && connection !== undefined && (
              <span className="hidden sm:inline">
                <ConnectionPill
                  googleConnected={googleConnected}
                  overlaySyncing={overlaySyncing}
                  state={connection}
                  syncAge={syncAge}
                />
              </span>
            )}
            {!readOnly && chrome !== null && (
              <>
                {prefs.showSearchButton && (
                  <Hint label="Buscar (⌘K)">
                    <button
                      type="button"
                      aria-label="Buscar (⌘K)"
                      onClick={chrome.openPalette}
                      className="dream-icon-btn size-10"
                    >
                      <IconSearch className="size-4" />
                    </button>
                  </Hint>
                )}
                <OverflowMenu
                  onSettings={() => void navigate({ to: "/configuracoes", search: {} })}
                  onCommandPalette={chrome.openPalette}
                  onHelp={chrome.openHelp}
                  onLogout={() => void chrome.signOut()}
                />
              </>
            )}
            {!readOnly && props.onShare !== undefined && (
              <Hint label="Compartilhar">
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={() => {
                    setFiltersOpen(false);
                    props.onShare?.();
                  }}
                  aria-label="Compartilhar"
                  className="size-10"
                >
                  <IconShare className="size-4" />
                </Button>
              </Hint>
            )}
            {!readOnly && props.onCreate !== undefined && prefs.showCreateButton && (
              <Hint label="Nova entrega">
                <Button
                  variant="primary"
                  size="icon"
                  onClick={() => {
                    setFiltersOpen(false);
                    props.onCreate?.();
                  }}
                  aria-label="Nova entrega"
                  className="size-10"
                >
                  <IconPlus className="size-4" />
                </Button>
              </Hint>
            )}
          </div>
        </div>

        <ViewSwitcher
          compact={compact}
          onChange={props.onViewChange}
          readOnly={readOnly}
          view={view}
        />

        {!readOnly &&
        (googleConnected || fonte !== "todas") &&
        props.onFonteChange !== undefined ? (
          <SourceSwitcher compact={compact} fonte={fonte} onChange={props.onFonteChange} />
        ) : null}

        <div className="ml-auto hidden shrink-0 items-center gap-2 lg:flex">
          {!compact && connection !== undefined && (
            <ConnectionPill
              googleConnected={googleConnected}
              overlaySyncing={overlaySyncing}
              state={connection}
              syncAge={syncAge}
            />
          )}
          <span className="hidden shrink-0 text-[12px] tabular-nums text-[var(--color-ink-muted)] xl:inline">
            {loading ? "carregando..." : countLabel}
          </span>
          {!readOnly && chrome !== null && (
            <>
              {prefs.showSearchButton && (
                <Hint label="Buscar (⌘K)">
                  <button
                    type="button"
                    aria-label="Buscar (⌘K)"
                    onClick={chrome.openPalette}
                    className="dream-icon-btn size-9"
                  >
                    <IconSearch className="size-4" />
                  </button>
                </Hint>
              )}
              <OverflowMenu
                onSettings={() => void navigate({ to: "/configuracoes", search: {} })}
                onCommandPalette={chrome.openPalette}
                onHelp={chrome.openHelp}
                onLogout={() => void chrome.signOut()}
              />
              {prefs.showShortcutsButton && (
                <Hint label="Atalhos (⌘K)">
                  <button
                    type="button"
                    aria-label="Atalhos (⌘K)"
                    onClick={chrome.openPalette}
                    className="dream-icon-btn size-9"
                  >
                    <IconBolt className="size-4" />
                  </button>
                </Hint>
              )}
              {prefs.showAvailabilityButton && (
                <button
                  type="button"
                  aria-pressed={prefs.workingHoursEnabled}
                  onClick={() => setPrefs({ workingHoursEnabled: !prefs.workingHoursEnabled })}
                  className={[
                    "inline-flex h-[38px] items-center gap-1.5 rounded-lg border px-3 text-[14px] font-semibold",
                    "transition-[background-color,border-color,color,box-shadow] duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-standard)]",
                    prefs.workingHoursEnabled
                      ? "border-[#0074FF]/35 bg-[var(--color-surface)] text-[#0074FF] shadow-[var(--shadow-xs)]"
                      : "border-[var(--color-line)] text-[var(--color-ink)] hover:bg-[var(--color-surface-muted)]",
                  ].join(" ")}
                >
                  <IconClock className="size-4" />
                  Disponibilidade
                </button>
              )}
            </>
          )}
          {!readOnly && props.onShare !== undefined && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setFiltersOpen(false);
                props.onShare?.();
              }}
              className="h-[38px] shrink-0 rounded-lg px-3 text-[14px] font-semibold"
            >
              <IconShare className="size-4" />
              Compartilhar
            </Button>
          )}
          {!readOnly && props.onCreate !== undefined && prefs.showCreateButton && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setFiltersOpen(false);
                props.onCreate?.();
              }}
              className="h-[38px] shrink-0 rounded-lg px-4 text-[14px] font-semibold"
            >
              <IconPlus className="size-4" />
              Nova entrega
            </Button>
          )}
        </div>
      </div>

      <div className="relative flex items-center gap-2 px-3 pb-2 sm:gap-3 sm:px-4 lg:px-5">
        <div className="relative min-w-0 flex-1 lg:max-w-xs">
          <IconSearch className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-[var(--color-ink-muted)]" />
          <Input
            type="search"
            value={search}
            onChange={(e) => props.onSearchChange(e.target.value)}
            placeholder={readOnly ? "Buscar no seu calendário" : "Buscar entregas…"}
            aria-label="Buscar entregas"
            className="h-10 pl-8 text-[0.8125rem] lg:h-8"
          />
        </div>

        <div className="relative shrink-0">
          <Button
            ref={filtersButtonRef}
            variant={filtersOpen ? "subtle" : "secondary"}
            size="sm"
            aria-expanded={filtersOpen}
            aria-controls="calendar-filters"
            aria-haspopup="dialog"
            aria-label={filterCount > 0 ? `Filtros (${filterCount} ativos)` : "Filtros"}
            onClick={() => setFiltersOpen((open) => !open)}
            className="h-10 shrink-0 lg:h-8"
          >
            <IconFilter className="size-3.5" />
            Filtros
            {filterCount > 0 && (
              <span className="ml-0.5 inline-flex min-w-4 items-center justify-center rounded-full bg-[#0074FF] px-1 text-[10px] font-bold text-white">
                {filterCount}
              </span>
            )}
          </Button>

          {filtersOpen && (
            <div
              ref={filtersRef}
              id="calendar-filters"
              data-testid="calendar-filters"
              role="dialog"
              aria-label="Filtros do calendário"
              className="dream-mini-window dream-popover-in absolute right-0 top-[calc(100%+0.35rem)] z-40 w-[min(16.5rem,calc(100vw-1.5rem))] p-1.5"
            >
              {hasFilters && (
                <div className="mb-1 flex h-6 items-center justify-end">
                  <button
                    type="button"
                    aria-label="Limpar filtros"
                    onClick={() => {
                      props.onClearFilters();
                      setFiltersOpen(false);
                    }}
                    className="px-1 text-[11px] font-semibold text-[#0074FF] transition-opacity duration-[var(--dream-duration-fast)] hover:opacity-80"
                  >
                    Limpar
                  </button>
                </div>
              )}

              <div className="flex flex-col gap-1">
                <FilterField label="Status">
                  <MenuSelect
                    aria-label="Filtrar por status"
                    size="sm"
                    className="h-8"
                    value={statuses.length === 1 ? (statuses[0] ?? "") : ""}
                    onChange={(next) =>
                      props.onStatusesChange(next === "" ? [] : [next as DeliverableStatus])
                    }
                    options={[
                      { value: "", label: "Todos" },
                      ...DELIVERABLE_STATUSES.map((status) => ({
                        value: status,
                        label: DELIVERABLE_STATUS_LABELS[status],
                      })),
                    ]}
                  />
                </FilterField>

                {clients.length > 1 && (
                  <FilterField label="Cliente">
                    <MenuSelect
                      aria-label="Filtrar por cliente"
                      size="sm"
                      className="h-8"
                      value={clientIds.length === 1 ? (clientIds[0] ?? "") : ""}
                      onChange={(next) => props.onClientIdsChange(next === "" ? [] : [next])}
                      options={[
                        { value: "", label: "Todos" },
                        ...clients.map((client) => ({ value: client.id, label: client.name })),
                      ]}
                    />
                  </FilterField>
                )}

                {projects.length > 0 && props.onProjectIdsChange !== undefined && (
                  <FilterField label="Projeto">
                    <MenuSelect
                      aria-label="Filtrar por projeto ou campanha"
                      size="sm"
                      className="h-8"
                      value={projectIds.length === 1 ? (projectIds[0] ?? "") : ""}
                      onChange={(next) => props.onProjectIdsChange?.(next === "" ? [] : [next])}
                      options={[
                        { value: "", label: "Todos" },
                        ...projects
                          .filter(
                            (project) =>
                              clientIds.length === 0 || clientIds.includes(project.clientId),
                          )
                          .map((project) => {
                            const client = clients.find((item) => item.id === project.clientId);
                            const label =
                              clients.length > 1 && client !== undefined
                                ? `${client.name} — ${project.name}`
                                : project.name;
                            return { value: project.id, label };
                          }),
                      ]}
                    />
                  </FilterField>
                )}

                {types.length > 1 && props.onTypeIdsChange !== undefined && (
                  <FilterField label="Tipo">
                    <MenuSelect
                      aria-label="Filtrar por tipo"
                      size="sm"
                      className="h-8"
                      value={typeIds.length === 1 ? (typeIds[0] ?? "") : ""}
                      onChange={(next) => props.onTypeIdsChange?.(next === "" ? [] : [next])}
                      options={[
                        { value: "", label: "Todos" },
                        ...types.map((type) => ({
                          value: type.id,
                          label: type.label,
                          icon: <TypeIcon slug={type.slug} name={type.icon} size={14} />,
                        })),
                      ]}
                    />
                  </FilterField>
                )}

                {!readOnly && team.length > 0 && props.onAssigneeIdsChange !== undefined && (
                  <FilterField label="Resp.">
                    <MenuSelect
                      aria-label="Filtrar por responsável"
                      size="sm"
                      className="h-8"
                      value={assigneeIds.length === 1 ? (assigneeIds[0] ?? "") : ""}
                      onChange={(next) => props.onAssigneeIdsChange?.(next === "" ? [] : [next])}
                      options={[
                        { value: "", label: "Todos" },
                        ...team.map((member) => ({ value: member.id, label: member.name })),
                      ]}
                    />
                  </FilterField>
                )}
              </div>

              {!readOnly && (
                <div className="mt-1.5 flex flex-col gap-1 border-t border-[var(--color-line)] pt-1.5">
                  <FilterField label="Grupo">
                    <MenuSelect
                      aria-label="Agrupar por"
                      size="sm"
                      className="h-8"
                      value={groupBy}
                      onChange={(next) => props.onGroupByChange(next as GroupByOption)}
                      options={(Object.keys(GROUP_LABELS) as GroupByOption[]).map((option) => ({
                        value: option,
                        label: GROUP_LABELS[option],
                      }))}
                    />
                  </FilterField>

                  {clients.length > 1 && (
                    <FilterField label="Cor">
                      <MenuSelect
                        aria-label="Colorir por"
                        size="sm"
                        className="h-8"
                        value={colorBy}
                        onChange={(next) => props.onColorByChange(next as "type" | "client")}
                        options={[
                          { value: "type", label: "Por tipo" },
                          { value: "client", label: "Por cliente" },
                        ]}
                      />
                    </FilterField>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-10 shrink-0 lg:h-8"
            aria-label="Limpar filtros"
            onClick={props.onClearFilters}
          >
            Limpar
          </Button>
        )}

        <span className="ml-auto hidden shrink-0 text-[11px] tabular-nums text-[var(--color-ink-muted)] sm:inline lg:hidden">
          {loading ? "…" : googleCount > 0 ? `${resultCount}+${googleCount}` : `${resultCount}`}
        </span>
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-3 pb-2 sm:px-4 lg:px-5">
          {chips.map((chip) => (
            <button
              key={chip.dimension}
              type="button"
              aria-label={`Remover filtro ${chip.label}`}
              onClick={() => clearDimension(chip.dimension)}
              className="dream-enter inline-flex h-7 max-w-full items-center gap-1 rounded-full bg-[var(--color-surface-muted)] pl-2.5 pr-1 text-[12px] font-medium text-[var(--color-ink)] transition-[background-color,transform] duration-[var(--dream-duration-fast)] hover:bg-[var(--color-surface-inset)] active:scale-[0.98]"
            >
              <span className="min-w-0 truncate">{chip.label}</span>
              <span
                aria-hidden="true"
                className="flex size-5 shrink-0 items-center justify-center rounded-full text-[var(--color-ink-muted)]"
              >
                <IconClose className="size-3" />
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SourceSwitcher({
  fonte,
  compact,
  onChange,
}: {
  fonte: CalendarSource;
  compact: boolean;
  onChange: (fonte: CalendarSource) => void;
}) {
  const options: Array<{ id: CalendarSource; label: string; short: string }> = [
    { id: "todas", label: "Tudo", short: "Tudo" },
    { id: "entregas", label: "Entregas", short: "Entregas" },
    { id: "agenda", label: "Agenda", short: "Agenda" },
  ];
  const { listRef, pill } = useSlidingPill(`fonte:${fonte}:${compact ? "c" : "d"}`);

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label="Fonte do calendário"
      className="relative flex h-10 w-full min-w-0 shrink-0 items-center overflow-hidden rounded-lg bg-[var(--color-surface-subtle)] p-1 lg:h-8 lg:w-auto"
    >
      <span
        aria-hidden="true"
        data-ready={pill.ready ? "true" : "false"}
        className="dream-pill"
        style={{ left: pill.left, width: pill.width, top: pill.top, height: pill.height }}
      />
      {options.map((option) => {
        const selected = fonte === option.id;
        return (
          <Tooltip key={option.id}>
            <TooltipTrigger asChild>
              <button
                type="button"
                role="tab"
                aria-label={
                  option.id === "agenda"
                    ? "Só a Google Agenda"
                    : option.id === "entregas"
                      ? "Só entregas"
                      : "Entregas e Google Agenda"
                }
                aria-selected={selected}
                data-pill-active={selected ? "true" : undefined}
                onClick={() => onChange(option.id)}
                className={[
                  "relative z-[1] inline-flex h-full min-w-0 flex-1 items-center justify-center gap-1 rounded-md px-1.5 text-[11px] tracking-[0]",
                  "sm:px-2 sm:text-[12px] lg:flex-none lg:px-2.5",
                  "transition-[color,background-color] duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-out)]",
                  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
                  selected
                    ? "font-bold text-[#0074FF]"
                    : "font-medium text-[var(--color-ink-secondary)] hover:text-[var(--color-ink)]",
                ].join(" ")}
              >
                {option.id === "agenda" ? (
                  <span className="inline-flex shrink-0" aria-hidden="true">
                    <IconGoogle className="size-3.5" />
                  </span>
                ) : null}
                <span className="min-w-0 truncate">{compact ? option.short : option.label}</span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {option.id === "agenda"
                ? "Mostra só os compromissos da Google Agenda"
                : option.id === "entregas"
                  ? "Esconde o overlay da Agenda"
                  : "Entregas e Google Agenda juntos, na ordem do relógio"}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

function ViewSwitcher({
  view,
  compact,
  readOnly,
  onChange,
}: {
  view: CalendarView;
  compact: boolean;
  readOnly: boolean;
  onChange: (view: CalendarView) => void;
}) {
  const options = (["dia", "semana", "mes", "agenda", "linha"] as const).filter(
    (option) => !(readOnly && option === "linha"),
  );
  const { listRef, pill } = useSlidingPill(`${view}:${compact ? "c" : "d"}:${options.join(",")}`);

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label="Visualização do calendário"
      className="relative flex h-12 w-full min-w-0 shrink-0 items-center overflow-hidden rounded-lg bg-[var(--color-surface-subtle)] p-1 lg:w-auto"
    >
      <span
        aria-hidden="true"
        data-ready={pill.ready ? "true" : "false"}
        className="dream-pill"
        style={{ left: pill.left, width: pill.width, top: pill.top, height: pill.height }}
      />
      {options.map((option) => {
        const selected = view === option;
        const name = CALENDAR_VIEW_LABELS[option];
        return (
          <Tooltip key={option}>
            <TooltipTrigger asChild>
              <button
                type="button"
                role="tab"
                aria-label={name}
                aria-selected={selected}
                data-pill-active={selected ? "true" : undefined}
                onClick={() => onChange(option)}
                className={[
                  "relative z-[1] inline-flex h-full min-w-0 flex-1 items-center justify-center gap-1 rounded-md px-1 text-[11px] tracking-[0]",
                  "sm:gap-1.5 sm:px-2 sm:text-[13px] lg:flex-none lg:px-2.5 lg:text-[13px] xl:px-3 xl:text-[14px]",
                  "transition-[color,background-color] duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-out)]",
                  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
                  selected
                    ? "font-bold text-[#0074FF]"
                    : "font-medium text-[var(--color-ink-secondary)] hover:text-[var(--color-ink)]",
                ].join(" ")}
              >
                <span className="inline-flex shrink-0" aria-hidden="true">
                  {VIEW_ICONS[option]}
                </span>
                <span className="min-w-0 truncate max-[360px]:hidden">{name}</span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{name}</TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

function Hint({ label, children }: { label: string; children: ReactElement }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-[3.35rem] shrink-0 truncate text-[11px] font-medium leading-none text-[var(--color-ink-muted)]">
        {label}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
